import { defineStore } from 'pinia';
import MaterialService from '@/services/material_service';
import MaterialTipoService from '@/services/material_tipo_service';
import MaterialCategoriaService from '@/services/material_categoria_service';
import {
  Material,
  initial_filters,
} from '@/views/material/models/material_model';
import { getParamsUrl } from '../views/material/material_utils';
import api from '@/services/api';
import { useConfiguracionStore } from '@/stores/configuracion-general.js';
import { MaterialGenericoFiltroOptions } from '@/enum/material_generico_filtro.js';

export const useMaterialStore = defineStore('materialStore', {
  state: () => ({
    material: JSON.parse(JSON.stringify(Material)),
    camposDisponibles: [],
    filtersVersion: 0,
    materialList: [],
    filters: { ...initial_filters },
    materialProceso: [],
    materialProcesoPage: [],
    materialProcesoFinished: [],
    familiaMaterial: {},
    page: 1,
    pageMaterial: 1,
    categoriasPorProceso: {},
    materialesPorProceso: {},
  }),
  getters: {
    TieneMovimientoStock() {
      return (this.material && this.material.TieneMovimientoStock) || false;
    },
  },
  actions: {
    applyFilters(patch) {
      this.filters = { ...this.filters, ...patch };
      this.filtersVersion++;
    },
    async createMaterialRapidoHTTP(payload) {
      try {
        const response = await MaterialService.create(payload);
        if (response.status === 200 || response.status === 201) {
          return response.data || response;
        }
        return null;
      } catch (error) {
        console.error('Error al crear material rápido:', error);
        throw error;
      }
    },
    async findAllMaterialesHTTP(params = {}) {
      const result = await MaterialService.findAllWithQuery(
        getParamsUrl({ ...this.filters, limit: 30, page: +this.pageMaterial, ...params }),
      );
      if (result && result.data && result.data.length > 0) {
        this.materialList.push(...result.data);
        this.setPageMaterial(this.pageMaterial + 1);
        return true;
      }
      return false;
    },
    async findAndReturnAllMaterialesHTTP(params) {
      const result = await MaterialService.findAllWithQuery(
        getParamsUrl({ ...this.filters, ...params }),
      );
      if (result && result.data && result.data.length > 0) {
        return result.data;
      }
      return [];
    },
    async updateMaterialCostosHTTP(costos) {
      const response = await MaterialService.updateCosts(costos);
      if (response && response.data) {
        const { materials } = response.data;
        materials.forEach((updated) => {
          const idx = this.materialList.findIndex((m) => m.Codigo === updated.Codigo);
          if (idx !== -1) {
            this.materialList[idx].Costo = updated.Costo;
          }
        });
      }
      return response.data;
    },
    async findAllMaterialByFamiliaMaterial(params) {
      const result = await MaterialService.findByFamily(params);
      if (result && result.data && result.data.length > 0) {
        this.materialProceso = [];
        this.materialProceso.push(...result.data);
      }
    },
    async findAllMaterialesByTipoHTTP() {
      this.setPage(1);
      const result = await MaterialService.findByType(5, this.page);
      if (result && result.data) {
        this.materialList.push(...result.data);
        this.setPage(this.page + 1);
        return true;
      }
      return false;
    },
    async searchMaterialHTTP(query) {
      const result = await MaterialService.search(query, { page: 1, tipo: 5 });
      if (result && result.data && result.data.length > 0) {
        return [...result.data];
      }
    },
    async searchProductoTerminadoHTTP(query) {
      const result = await MaterialService.searchProductoTerminado(query, {
        page: 1,
        TipoMaterial: 'Producto Terminado',
      });
      if (result && result.data && result.data.length > 0) {
        return [...result.data];
      }
    },
    async fetchCategoriasPorProcesoElemento(procesoId, elementoTipoId, itemProceso) {
      const { data } = await api.get(`/proceso/${procesoId}/materiales`, {
        params: { elementoTipoId },
      });
      this.categoriasPorProceso[itemProceso] = data;
      this.materialesPorProceso[itemProceso] = {};

      for (const rel of data) {
        await this.findMaterialPorProceso(
          { categoria: rel.CodigoCategoriaMaterial, limit: 999 },
          itemProceso,
        );
        this.materialesPorProceso[itemProceso][rel.CodigoCategoriaMaterial] =
          this.materialProceso[itemProceso];
      }
    },
    async findMaterialPorProceso(filters = {}, indexProceso, page = 1, limit = 35) {
      if (!this.materialProceso[indexProceso]) {
        this.materialProceso[indexProceso] = [];
        this.materialProcesoPage[indexProceso] = 1;
        this.materialProcesoFinished[indexProceso] = false;
      }
      if (page > 1 && this.materialProcesoFinished[indexProceso]) return;

      const { data } = await MaterialService.findActiveMaterials(filters, page, limit);

      if (data) {
        if (page === 1) {
          this.materialProceso[indexProceso] = data;
        } else {
          const exist = this.materialProceso[indexProceso];
          data.forEach((m) => {
            if (!exist.find((e) => e.Codigo === m.Codigo)) {
              exist.push(m);
            }
          });
        }
        this.materialProcesoPage[indexProceso] = page + 1;
        if (data.length < limit) {
          this.materialProcesoFinished[indexProceso] = true;
        }
      }
    },
    async searchMaterialPorProceso(filters = {}, indexProceso, query, limit = 35) {
      this.materialProceso[indexProceso] = [];
      this.materialProcesoPage[indexProceso] = 2;
      this.materialProcesoFinished[indexProceso] = false;

      const { data } = await MaterialService.findActiveMaterials(
        { Descripcion: query, ...filters },
        1,
        limit,
      );
      if (data) {
        this.materialProceso[indexProceso] = data;
        if (data.length < limit) {
          this.materialProcesoFinished[indexProceso] = true;
        }
      }
    },
    async firstMaterial(params = {}) {
      this.setPageMaterial(1);
      this.emptyList();
      await this.findAllMaterialesHTTP(params);
    },
    resetMaterial() {
      this.material = JSON.parse(JSON.stringify(Material));
      this.camposDisponibles = [];
    },

    async loadCamposDisponiblesHTTP(codigoTipo) {
      if (!codigoTipo) {
        this.camposDisponibles = [];
        return this.camposDisponibles;
      }
      try {
        const result = await MaterialService.findCamposDisponibles(codigoTipo);
        this.camposDisponibles = Array.isArray(result?.data) ? result.data : [];
      } catch (error) {
        console.error('Error en loadCamposDisponiblesHTTP:', error);
        this.camposDisponibles = [];
      }
      return this.camposDisponibles;
    },
    resetFilter() {
      const { filtroGenericoMaterialDefecto } = useConfiguracionStore();
      const genericoDefault =
        MaterialGenericoFiltroOptions.find(
          (opcion) => opcion.Codigo === filtroGenericoMaterialDefecto,
        ) || MaterialGenericoFiltroOptions[0];

      this.filters = {
        ...initial_filters,
        GenericoFiltro: genericoDefault,
        filtroGenerico: genericoDefault.Codigo,
      };
    },
    async saveMaterial() {
      if (this.material.Codigo) {
        await this.updateMaterialHTTP();
      } else {
        await this.createMaterialHTTP();
      }
    },
    async createMaterialHTTP() {
      // Crear una copia sin propiedades que no deben ser enviadas
      const { CodigoMaterialCategoria, Color, TieneMovimientoStock, ...materialPayload } =
        this.material;
      const response = await MaterialService.create(materialPayload);
      if (response.status === 200 || response.status === 201) {
        this.resetMaterial();
        return;
      }
    },
    async updateMaterialHTTP() {
      // Crear una copia sin propiedades que no deben ser enviadas
      const { CodigoMaterialCategoria, Color, TieneMovimientoStock, ...materialPayload } =
        this.material;

      // AtributosAdicionales ya viene como objeto desde el formulario
      // Se envía directamente al backend

      const response = await MaterialService.update(materialPayload.Codigo, materialPayload);
      if (response.status === 200 || response.status === 201) {
        this.resetMaterial();
        return;
      }
    },
    async findMaterialByCodigoHTTP(codigo) {
      try {
        const result = await MaterialService.findByCode(codigo);
        const material = result?.data ?? null;
        if (material) {
          this.camposDisponibles = material.CamposDisponibles ?? [];
          delete material.CamposDisponibles;

          // El backend devuelve AtributosAdicionales como objeto
          // Asegurar que esté inicializado
          if (!material.AtributosAdicionales) {
            material.AtributosAdicionales = {};
          }

          this.material = material;
        }
        return material;
      } catch (error) {
        console.error('Error en findMaterialByCodigoHTTP:', error);
        return null;
      }
    },
    setFamiliaMaterial({ propiedad, valor }) {
      this.material.MateriaPrimaFamilia[propiedad] = valor;
    },
    setCodigosMaterial({ propiedad, valor }) {
      this.material[propiedad] = valor;
    },
    setPage(page) {
      this.page = page;
    },
    setPageMaterial(page) {
      this.pageMaterial = page;
    },
    emptyList() {
      this.materialList = [];
    },
    emptyMaterialProceso() {
      this.materialProceso = [];
    },
    emptyMaterialProcesoSustrato(indexList) {
      if (Array.isArray(indexList)) {
        indexList.forEach((i) => {
          this.materialProceso[i] = [];
        });
      } else {
        return;
      }
    },
    /**
     * Obtiene todas las categorías de materiales disponibles
     * @returns {Promise<Array>} Array de categorías
     */
    async findAllCategoriasHTTP() {
      try {
        const result = await MaterialCategoriaService.findActive();
        if (result && result.data) {
          return Array.isArray(result.data) ? result.data : [];
        }
        return [];
      } catch (error) {
        console.error('Error al obtener categorías de materiales:', error);
        return [];
      }
    },

    /**
     * Busca categorías de materiales por descripción
     * @param {string} query - Término de búsqueda
     * @returns {Promise<Array>} Array de categorías que coinciden con la búsqueda
     */
    async searchCategoriasHTTP(query) {
      try {
        const result = await MaterialCategoriaService.search(query, { activo: true });
        if (result && result.data) {
          return Array.isArray(result.data) ? result.data : [];
        }
        return [];
      } catch (error) {
        console.error('Error al buscar categorías de materiales:', error);
        return [];
      }
    },

    /**
     * Obtiene una categoría de material por su código
     * @param {string|number} codigo - Código de la categoría
     * @returns {Promise<Object|null>} Objeto de la categoría o null si no se encuentra
     */
    async findCategoriaByCodigoHTTP(codigo) {
      try {
        const result = await MaterialCategoriaService.findByCode(codigo);
        if (result && result.data) {
          return result.data;
        }
        return null;
      } catch (error) {
        console.error('Error al obtener categoría por código:', error);
        return null;
      }
    },
    async buscarProductosConETHTTP(params) {
      try {
        const { data } = await api.get(`/material/buscar-producto-et`, { params });
        return data || [];
      } catch (error) {
        console.error('Error al buscar productos con ET:', error);
        return [];
      }
    },
  },
});
